import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { filter, map, take, tap } from 'rxjs/operators';
import { AuthService, NavigationService, ProviderService } from '@app/core/services';
import { ApplicationInsightsService, AppInsightTags } from '@app/core/services/application-insights.service';
import { environment } from 'src/environments/environment';
import { PracticeAgentBridgeService } from './practice-agent-bridge.service';
import { PRACTICE_AGENT_SIDEBAR_BUTTON_ID, PracticeAgentWidgetControlService } from './practice-agent-widget-control.service';

@Component({
  selector: 'app-practice-agent-widget',
  templateUrl: './practice-agent-widget.component.html',
  styleUrls: ['./practice-agent-widget.component.scss'],
  providers: [PracticeAgentBridgeService],
})
export class PracticeAgentWidgetComponent implements OnInit, OnDestroy {
  @ViewChild('practiceAgentIframe') private practiceAgentIframe: ElementRef<HTMLIFrameElement>;
  @ViewChild('practiceAgentWidget') private practiceAgentWidget: ElementRef<HTMLElement>;
  @ViewChild('widgetSurface') private widgetSurface: ElementRef<HTMLElement>;
  @ViewChild('notificationBadge') private notificationBadge: ElementRef<HTMLElement>;

  readonly enabled$ = this.bridgeService.enabled$;
  readonly visible$ = this.bridgeService.visible$;
  readonly expanded$ = this.bridgeService.expanded$;
  readonly widgetStatus$ = this.bridgeService.widgetStatus$;
  readonly badgeMoving$ = this.widgetControlService.badgeMoving$;
  readonly sidebarOpen$ = this.bridgeService.sidebarOpen$;
  readonly wideSidebarOpen$ = this.bridgeService.wideSidebarOpen$;
  readonly widgetUrl: SafeResourceUrl | null;
  collapsed = false;
  minimizing = false;
  reopening = false;
  notificationCount: number | null = null;
  private animation: Animation | null = null;
  private badgeAnimation: Animation | null = null;
  private collapseAnimation: Animation | null = null;
  private readonly subscriptions = new Subscription();

  constructor(
    public bridgeService: PracticeAgentBridgeService,
    sanitizer: DomSanitizer,
    private widgetControlService: PracticeAgentWidgetControlService,
    private authService: AuthService,
    private providerService: ProviderService,
    private navigationService: NavigationService,
    private appInsightsService: ApplicationInsightsService
  ) {
    const validatedWidgetUrl = this.bridgeService.validatedWidgetUrl;
    this.widgetUrl = validatedWidgetUrl ? sanitizer.bypassSecurityTrustResourceUrl(validatedWidgetUrl) : null;
  }

  ngOnInit(): void {
    this.bridgeService.start();
    this.subscriptions.add(this.widgetControlService.notificationCount$.subscribe(count => {
      this.notificationCount = count;
    }));
    this.subscriptions.add(this.widgetControlService.collapsed$.subscribe(collapsed => {
      if (collapsed !== this.collapsed) {
        this.animateCollapsedBadge(collapsed);
      }
    }));
    this.subscriptions.add(this.widgetControlService.openRequested$.subscribe(() => {
      if (this.minimizing) {
        this.cancelAnimation();
      }
    }));
    this.subscriptions.add(this.visible$.subscribe(visible => {
      if (visible) {
        this.reopen();
      } else {
        this.cancelAnimation();
      }
    }));
    this.subscriptions.add(this.enabled$.subscribe(enabled => {
      if (!enabled) {
        this.cancelAnimation();
      }
    }));
  }

  onIframeLoad(): void {
    if (this.practiceAgentIframe) {
      this.bridgeService.connectIframe(this.practiceAgentIframe.nativeElement);
    }
  }

  toggleCollapsed(): void {
    this.widgetControlService.setCollapsed(!this.collapsed);
    this.bridgeService.trackCollapsed(this.collapsed);
  }

  private async animateCollapsedBadge(collapsed: boolean): Promise<void> {
    if (this.collapseAnimation && this.collapseAnimation.playState !== 'finished') {
      this.collapsed = collapsed;
      this.collapseAnimation.reverse();
      return;
    }

    this.cancelAnimation();
    const animation = this.notificationCount > 0 ? this.createSidebarAnimation(!collapsed, true) : null;
    this.collapsed = collapsed;
    if (!animation) {
      return;
    }

    this.collapseAnimation = animation;
    this.widgetControlService.setBadgeMoving(true);
    try {
      await animation.finished;
    } catch {
      return;
    }
    if (this.collapseAnimation === animation) {
      this.cancelAnimation();
    }
  }

  snooze(): void {
    this.minimize('snooze');
  }

  dismiss(): void {
    this.minimize('dismiss');
  }

  retry(): void {
    this.widgetControlService.openWidget();
  }

  openPracticeAgent(): void {
    const token = this.authService.token;
    const url = environment.practiceAgent.practiceAgentLink;
    this.providerService.provider$
      .pipe(
        take(1),
        filter(provider => !!token && !!provider?.PracticeId && !!provider.PracticeTenantId),
        map(provider => ({
          TreatingDoctorName: provider.TreatingDoctorName,
          PracticeNumber: provider.PracticeNumber,
          PracticeTenantId: provider.PracticeTenantId,
          PracticeId: provider.PracticeId,
          PracticeXRef: provider.PracticeXRef,
          HPCSANumber: provider.HPCSANumber,
        })),
        tap(user => {
          const opened = this.navigationService.openExternalAppWithAuth(url, url + '/', token, user);
          if (opened) {
            this.appInsightsService.trackEvent(AppInsightTags.PRACTICE_AGENT_EXPERIMENTS_OPENED, {
              tenantId: user.PracticeTenantId,
              referrer: 'practice-agent-widget',
              environment: environment.practiceAgent.environment,
            });
          }
        })
      )
      .subscribe();
  }

  private async minimize(action: 'dismiss' | 'snooze'): Promise<void> {
    if (this.minimizing) {
      return;
    }

    const button = document.getElementById(PRACTICE_AGENT_SIDEBAR_BUTTON_ID);
    this.cancelAnimation();
    this.minimizing = true;
    const animation = this.collapsed ? null : this.createSidebarAnimation(false);
    if (!animation) {
      this.finishMinimize(action, button);
      return;
    }

    this.animation = animation;
    try {
      await animation.finished;
    } catch {
      return;
    }
    if (this.animation === animation) {
      this.finishMinimize(action, button);
    }
  }

  private async reopen(): Promise<void> {
    this.cancelAnimation();
    this.reopening = true;
    const animation = this.createSidebarAnimation(true);
    if (!animation) {
      this.reopening = false;
      return;
    }

    this.animation = animation;
    try {
      await animation.finished;
    } catch {
      return;
    }
    if (this.animation === animation) {
      this.cancelAnimation();
    }
  }

  private createSidebarAnimation(reopening: boolean, badgeOnly = false): Animation | null {
    const widget = this.practiceAgentWidget?.nativeElement;
    const surface = this.widgetSurface?.nativeElement;
    const button = document.getElementById(PRACTICE_AGENT_SIDEBAR_BUTTON_ID);
    const target = button?.getBoundingClientRect();
    if (!surface?.animate || !target?.width || !target.height ||
        window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return null;
    }

    const source = widget.getBoundingClientRect();
    const deltaX = target.left + target.width / 2 - source.left - source.width / 2;
    const deltaY = target.top + target.height / 2 - source.top - source.height / 2;
    const frames: Keyframe[] = [
      { transform: 'translate(0, 0) scale(1)', opacity: 1, offset: 0 },
      { transform: `translate(${deltaX * 0.35}px, ${deltaY * 0.3 - 9}px) scale(.7)`, opacity: 1, offset: 0.42 },
      { transform: `translate(${deltaX * 0.9}px, ${deltaY * 0.86}px) scale(.2)`, opacity: 0.8, offset: 0.83 },
      { transform: `translate(${deltaX}px, ${deltaY}px) scale(.07)`, opacity: 0, offset: 1 },
    ];

    const timing: KeyframeAnimationOptions = {
      duration: 440,
      direction: reopening ? 'reverse' : 'normal',
      easing: reopening ? 'cubic-bezier(.45, 0, .8, .45)' : 'cubic-bezier(.4, 0, .2, 1)',
      fill: 'both',
    };

    const badge = this.notificationBadge?.nativeElement;
    // Material owns this badge element; recheck the selector when upgrading Angular Material.
    const iconBadge = button.querySelector('.mat-badge-content')?.getBoundingClientRect();
    if (badge) {
      const badgeRect = badge.getBoundingClientRect();
      const icon = button.querySelector('mat-icon')?.getBoundingClientRect() || target;
      const badgeCenterX = badgeRect.left + badgeRect.width / 2;
      const badgeCenterY = badgeRect.top + badgeRect.height / 2;
      const cornerX = badgeCenterX - source.left - source.width / 2;
      const cornerY = badgeCenterY - source.top - source.height / 2;
      const badgeDeltaX = (iconBadge?.width ? iconBadge.left + iconBadge.width / 2 : icon.right) - badgeCenterX;
      const badgeDeltaY = (iconBadge?.height ? iconBadge.top + iconBadge.height / 2 : icon.top) - badgeCenterY;

      // Follow the shrinking card corner, keeping the count readable until it lands on the icon.
      this.badgeAnimation = badge.animate([
        {
          transform: 'translate(0, 0) scale(1.18)',
          offset: 0,
        },
        {
          transform: `translate(${deltaX * 0.35 - cornerX * 0.3}px, ${deltaY * 0.3 - 9 - cornerY * 0.3}px) scale(1.12)`,
          offset: 0.42,
        },
        {
          transform: `translate(${deltaX * 0.9 - cornerX * 0.8}px, ${deltaY * 0.86 - cornerY * 0.8}px) scale(1.035)`,
          offset: 0.83,
        },
        {
          transform: `translate(${badgeDeltaX}px, ${badgeDeltaY}px) scale(1)`,
          offset: 1,
        },
      ], timing);
    }

    return badgeOnly ? this.badgeAnimation : surface.animate(frames, timing);
  }

  private finishMinimize(action: 'dismiss' | 'snooze', button: HTMLElement): void {
    // Hide before releasing the final animation frame so the iframe cannot flash back.
    this.minimizing = false;
    this.widgetControlService.setCollapsed(false);
    this.bridgeService[action]();
    this.cancelAnimation();
    button?.focus({ preventScroll: true });
  }

  @HostListener('window:resize')
  handleResize(): void {
    if (this.minimizing && this.animation) {
      this.badgeAnimation?.finish();
      this.animation.finish();
    } else {
      this.cancelAnimation();
    }
  }

  cancelAnimation(): void {
    this.minimizing = false;
    this.reopening = false;
    this.animation?.cancel();
    this.animation = null;
    this.badgeAnimation?.cancel();
    this.badgeAnimation = null;
    this.collapseAnimation = null;
    this.widgetControlService.setBadgeMoving(false);
  }

  ngOnDestroy(): void {
    this.cancelAnimation();
    this.subscriptions.unsubscribe();
    this.bridgeService.destroy();
  }
}
